import { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { destinationsApi } from '@/api/places.api';
import DataState from '@/components/common/DataState';
import { useNotify } from '@/components/common/useNotify';
import { useAuth } from '@/auth/useAuth';
import { typeAhead, TYPE_TO_SEARCH } from '@/components/common/SearchSelect';

type Opt = { code: string; name: string };

/**
 * Settings › Where we operate: the Indian states and other countries chosen at sign-up. Only these show when adding a
 * destination (and everything placed in one). A place that already holds destinations cannot be taken off.
 */
export function OperatingRegionsSection() {
  const qc = useQueryClient();
  const notify = useNotify();
  const { hasAnyPermission } = useAuth();
  const canEdit = hasAnyPermission(['Company.Edit']);
  const regions = useQuery({ queryKey: ['operating-regions'], queryFn: () => destinationsApi.operatingRegions() });
  const india = useQuery({ queryKey: ['places-geo', 'IN'], queryFn: () => destinationsApi.geo('IN'), staleTime: 600_000 });
  const [states, setStates] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  useEffect(() => { if (regions.data) { setStates(regions.data.stateCodes); setCountries(regions.data.countryCodes); } }, [regions.data]);

  const stateOpts: Opt[] = useMemo(() => (india.data?.states ?? []).map((s) => ({ code: s.code, name: s.name })), [india.data]);
  const countryOpts: Opt[] = useMemo(() => (india.data?.countries ?? []).filter((c) => c.iso2 !== 'IN').map((c) => ({ code: c.iso2, name: c.name })), [india.data]);
  const lockedStates = new Set(regions.data?.statesInUse ?? []);
  const lockedCountries = new Set(regions.data?.countriesInUse ?? []);
  const changed = !!regions.data && (states.join() !== regions.data.stateCodes.join() || countries.join() !== regions.data.countryCodes.join());
  const empty = states.length === 0 && countries.length === 0;

  const save = useMutation({ mutationFn: () => destinationsApi.saveOperatingRegions({ stateCodes: states, countryCodes: countries }) });

  const picker = (label: string, help: string, opts: Opt[], value: string[], set: (v: string[]) => void, locked: Set<string>) => (
    <Box>
      <Autocomplete multiple size="small" options={opts} disableCloseOnSelect disabled={!canEdit}
        value={opts.filter((o) => value.includes(o.code))}
        onChange={(_, v) => set([...v.map((o) => o.code), ...value.filter((c) => locked.has(c) && !v.some((o) => o.code === c))])}
        getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.code === b.code}
        filterOptions={typeAhead((o: Opt) => o.name, (o) => o.code)} noOptionsText={TYPE_TO_SEARCH}
        renderTags={(v, getTagProps) => v.map((o, i) => {
          const { key, onDelete, ...rest } = getTagProps({ index: i });
          return <Chip key={key} size="small" label={o.name} {...rest} onDelete={locked.has(o.code) ? undefined : onDelete} title={locked.has(o.code) ? 'Has destinations, so it stays' : undefined} />;
        })}
        renderInput={(p) => <TextField {...p} label={label} placeholder={value.length ? '' : 'Choose…'} helperText={help} />} />
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>Where we operate</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Chosen when you signed up. Only these states and countries show when you add a destination. Add a new one here whenever you start operating there.
        </Typography>
        <DataState loading={regions.isLoading || india.isLoading} error={regions.error ?? india.error} onRetry={() => { regions.refetch(); india.refetch(); }}>
          <Stack spacing={2.5} sx={{ maxWidth: 720 }}>
            {picker('India: states', 'Destinations in India can be added only in these states.', stateOpts, states, setStates, lockedStates)}
            {picker('Other countries', 'Every state or province of these countries is open.', countryOpts, countries, setCountries, lockedCountries)}
            {(lockedStates.size > 0 || lockedCountries.size > 0) && (
              <Typography variant="body2" color="text.secondary">A state or country that already has destinations cannot be taken off.</Typography>
            )}
            {empty && <Alert severity="warning">Choose at least one Indian state or one country.</Alert>}
            {canEdit && (
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={!changed || empty || save.isPending} onClick={async () => {
                  try {
                    await save.mutateAsync();
                    qc.invalidateQueries({ queryKey: ['operating-regions'] });
                    qc.invalidateQueries({ queryKey: ['places-geo-operating'] });
                    qc.invalidateQueries({ queryKey: ['destination-geo-operating'] });
                    notify.success('Saved. Destinations can now be added in these places.');
                  } catch (e) { notify.error(e); }
                }}>{save.isPending ? 'Saving…' : 'Save'}</Button>
                <Button variant="outlined" color="inherit" disabled={!changed} onClick={() => { setStates(regions.data!.stateCodes); setCountries(regions.data!.countryCodes); }}>Undo</Button>
              </Stack>
            )}
          </Stack>
        </DataState>
      </CardContent>
    </Card>
  );
}
